import { onMount } from 'svelte';
import { type Writable, writable, get, derived } from 'svelte/store';
import { StoredCard } from '../utils/local-storage';

type StorageType = 'sessionStorage' | 'localStorage';

class ReferrerStore implements Writable<string> {
  private storageType: StorageType;
  private key: string;
  private ttl: number;
  private store = writable(''); // svelte store

  /**
   *
   * @param name {string} name of the referrer store, dictate the storage key
   * @param storageType {string} 'sessionStorage' | 'localStorage'
   * @param ttl {number} time to live in millisecond
   */
  constructor(name: string, storageType: StorageType = 'sessionStorage', ttl = 7776000000) {
    this.storageType = storageType;
    this.ttl = ttl;
    this.key = `${name}_REFERRER`;
    // upon client code loaded, store is constructed, restore to get value from session/local storage
    this.restore();
  }
  get storage(): Storage | undefined {
    if (typeof window !== 'undefined') return window[this.storageType] as Storage;
    return undefined;
  }
  get storageValue(): string {
    // in server side, storage not availabel
    if (!this.storage) return '';
    try {
      // get from storage
      const { value, ts } = JSON.parse(this.storage.getItem(this.key) as string);
      // if ttl is not zero, and data expired return empty string
      if (this.ttl && Date.now() > ts + this.ttl) return '';

      return value;
    } catch {
      return '';
    }
  }
  restore(): void {
    // if store doesn't have value yet, use value from session/local storage
    if (!get(this.store)) this.store.set(this.storageValue);
  }
  set(value: string): void {
    this.storage?.setItem(this.key, JSON.stringify({ value, ts: Date.now() }));
    this.store.set(value);
    StoredCard.clear();
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  subscribe(cb: (value: any) => void): () => void {
    return this.store.subscribe(cb);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  update(cb: (value: any) => any): void {
    return this.store.update(cb);
  }
}

// keep these private and use accessCode
const queryReferrer = new ReferrerStore('QUERY', 'sessionStorage');
const pageReferrer = new ReferrerStore('PAGE', 'localStorage', 0);
const documentReferrer = new ReferrerStore('DOCUMENT', 'localStorage');

// if you need something auto update or svelte reactive, use this store
export const accessCode = derived([queryReferrer, pageReferrer, documentReferrer], (storeValues) =>
  storeValues.reduce((accValue, storeValue) => {
    return accValue || storeValue;
  }, '')
);

/**
 * set referrer from query params during onMount, this should only be called in layout
 * query referrer got stored unless it is empty
 */
export const setQueryReferrer = (): void => {
  onMount(() => {
    const { searchParams } = new URL(window.location.href);
    const value = searchParams?.get('utm_term') || searchParams?.get('source') || searchParams?.get('utm_campaign');
    if (value) queryReferrer.set(value);
  });
};

/**
 * set referrer from page during onMount, this should only be called in page that has referrer in CMS
 * page got stored unless it is empty
 */
export const setPageReferrer = (value?: string): void => {
  if (value) onMount(() => pageReferrer.set(value));
};

/**
 * set referrer from document.referrer during onMount, this should only be called in layout
 * document referrer got stored only if it is not our host domain
 */
export const setDocumentReferrer = (): void => {
  onMount(() => {
    try {
      if (document.referrer) {
        const value = new URL(document.referrer).host;
        const { host } = new URL(window.location.href);
        if (!value.match(host)) documentReferrer.set(value);
      }
    } catch (err) {
      console.error(err); /* eslint-disable-line no-console */
    }
  });
};

/**
 * append accessCode query string to absolute url
 * @param url absolute url to append accessCode as query string
 */
export const getUrlWithAccessCode = (url: string): string => {
  const code = get(accessCode);
  if (!code) return url;

  try {
    const urlObj = new URL(url);
    urlObj.searchParams.set('accessCode', code);
    return urlObj.toString();
  } catch {
    return url;
  }
};
